import React, { useState } from "react";
import { Link } from "react-router-dom";

type FeatureCard = {
  title: string;
  description: string;
  tone: string;
  icon: string;
};

type ModuleCard = {
  title: string;
  image: string;
  tone: string;
  features: string[];
};

const features: FeatureCard[] = [
  {
    title: "Reliable Performance",
    description:
      "Built on reliable cloud infrastructure to ensure uninterrupted access and smooth business operations anytime you need it.",
    tone: "blue",
    icon: "◷",
  },
  {
    title: "Connected Anytime, Anywhere",
    description:
      "Access your team Performance,Material movement over different sections in real time through a fully cloud-enabled platform.",
    tone: "indigo",
    icon: "◎",
  },
  {
    title: "Advanced Data Security",
    description:
      "Built with secure authentication, access controls and encrypted connections to keep your business data protected.",
    tone: "green",
    icon: "◇",
  },
  {
    title: "Industry-Specific ERP",
    description:
      "Built exclusively for seafood export operations with industry-specific workflows.",
    tone: "cyan",
    icon: "▦",
  },
  {
    title: "Lot-Wise Traceability",
    description:
      "Track inventory, yield and batch movement in real time.",
    tone: "orange",
    icon: "⬡",
  },
  {
    title: "Live Inventory Control",
    description:
      "Monitor stock, storage and production instantly.",
    tone: "teal",
    icon: "◈",
  },
  {
    title: "Smart Billing & Finance",
    description:
      "Automate billing, TDS and financial processes.",
    tone: "pink",
    icon: "▣",
  },
  {
    title: "Labor Cost Tracking",
    description:
      "Manage wages and operational costs efficiently.",
    tone: "lime",
    icon: "$",
  },
  {
    title: "Custom Documentation",
    description:
      "Generate buyer-specific export and compliance documents.",
    tone: "rose",
    icon: "▤",
  },
];

const modules: ModuleCard[] = [
  {
    title: "Purchase",
    image: "/images/purchase-module-image.png",
    tone: "blue",
    features: [
      "Daily Procurement Management",
      "Supplier Management",
      "Built-in TDS Compliance",
      "Automated Invoicing",
      "Comprehensive Reports",
    ],
  },
  {
    title: "Pre-Processing",
    image: "/images/pre-processing-module-image.png",
    tone: "indigo",
    features: [
      "Batch Tracking",
      "Stage-wise Yield Analysis",
      "Floor Balance Tracking",
      "Transparent Labour Costing",
      "Customised Reports",
    ],
  },
  {
    title: "Production",
    image: "/images/production-module-image.png",
    tone: "purple",
    features: [
      "IQF & Block Freezing",
      "Yield Tracking",
      "Production Monitoring",
      "HON to finished product analysis",
      "Comprehensive reports",
    ],
  },
  {
    title: "Quality Control",
    image: "/images/quality-control-module.png",
    tone: "blue",
    features: [
      "Antibiotic Analysis",
      "Microbial Testing",
      "QC Checklist Observations",
      "EIA-Specific Documentation",
      "Kits and Media Tracking",
      "QC Audits",
    ],
  },
  {
    title: "Cold Storage",
    image: "/images/cold-store-module-image.png",
    tone: "green",
    features: [
      "Digital Store View",
      "Live Inventory Monitoring",
      "FIFO/LIFO Management",
      "RM Requirement Analysis",
      "Customised Reports",
    ],
  },
  {
    title: "Sales",
    image: "/images/sales-module-image.png",
    tone: "cyan",
    features: [
      "Automated Invoicing",
      "Buyer-Specific Documentation",
      "PI to Payment Settlement Tracking",
      "Antidumping Analysis",
      "Customised Reports",
    ],
  },
  {
    title: "Inventory",
    image: "/images/inventory-module-image.png",
    tone: "purple",
    features: [
      "Vendor Management",
      "Inventory Management",
      "General & Packing Costing Analysis",
      "Indent - to -material issue workflow",
      "Vendor price quotation comparison",
    ],
  },
  {
    title: "HR",
    image: "/images/hrms-module-image.png",
    tone: "blue",
    features: [
      "Biometric Device Integration",
      "Live Attendance Tracking",
      "Employee Shift Scheduling",
      "Payroll Automation",
      "Expense & Reimbursement Management",
    ],
  },
];

const brands = [
  { name: "Devi Fisheries Limited", image: "/images/brands/devi-fisheries.png" },
  { name: "Coastal Aqua Pvt. Ltd", image: "/images/brands/coastal-aqua.png" },
  { name: "Jagadeesh Marine Exports", image: "/images/brands/jagadeesh-marines.png" },
  { name: "Suryamitra Exim Pvt. Ltd", image: "/images/brands/surya-mitra.png" },
  { name: "Sai Marine Exports", image: "/images/brands/sai-marines.png" },
  { name: "Mourya Aquex Pvt. Ltd", image: "/images/brands/mourya.png" },
  { name: "Jayalakshmi Sea Foods Pvt. Ltd", image: "/images/brands/jaya-lakshmi-sea-foods.png" },
];

export default function LandingPage() {
  const [showVideo, setShowVideo] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <div className="cam-live-landing">
      <header className="cam-live-header">
        <div className="header-container">
          <a href="#home" className="cam-live-logo-link">
            <img
              src="/brand/camaroo-logo.png"
              alt="Camaroo ERP"
              className="cam-live-logo"
            />
          </a>

          <nav className="cam-live-nav">
            <a href="#features">Features</a>
            <a href="#modules">Modules</a>
            <a href="#contact">Contact</a>
          </nav>

          <Link to="/auth-login" className="cam-live-login">
            Login
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </header>

      <main>
        <section id="home" className="hero-section">
          <div className="hero-bg" />

          <div className="hero-container">
            <div className="hero-grid">
              <div className="hero-content">
                <div className="cam-live-hero-chip">
                  <span>★</span>
                  Digitise Every Step of Your Shrimp Export Journey
                </div>

                <h1 className="hero-title">
                  Digitise Your
                  <br />
                  <span className="cam-live-gradient-text">
                    Seafood Export Business
                  </span>
                </h1>

                <p className="hero-description">
                  Camaroo is a smart ERP built for seafood exporters,
                  streamlining operations from procurement to payments.
                  Simplify workflows, improve traceability, reduce losses and
                  drive business growth with real-time control.
                </p>
              </div>

              <div className="hero-image">
                <div className="hero-image-container">
                  <video
                    className="cam-live-video"
                    src="/assets/videos/demo-video.mp4"
                    muted
                    playsInline
                    controls={showVideo}
                    autoPlay={showVideo}
                  />

                  {!showVideo && (
                    <>
                      <div className="hero-image-overlay" />

                      <button
                        type="button"
                        className="hero-play-button"
                        onClick={() => setShowVideo(true)}
                        aria-label="Play Camaroo ERP demo"
                      >
                        <span className="cam-live-play-icon">▶</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="cam-live-section cam-live-features-section">
          <div className="cam-live-section-container">
            <div className="section-header">
              <h2>
                Why Seafood Exporters{" "}
                <span className="cam-live-gradient-text">
                  Choose Camaroo
                </span>
              </h2>
              <p>Experience the Camaroo Difference</p>
            </div>

            <div className="features-grid cam-live-features-grid">
              {features.map((feature) => (
                <article
                  className={`feature-card cam-feature-${feature.tone}`}
                  key={feature.title}
                >
                  <div
                    className={`feature-icon-container cam-icon-${feature.tone}`}
                  >
                    <span className="feature-icon">{feature.icon}</span>
                  </div>

                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">
                    {feature.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="cam-live-brands-section">
          <div className="cam-live-section-container">
            <div className="section-header cam-brand-header">
              <h2>
                Trusted by{" "}
                <span className="cam-live-gradient-text">
                  Industry Leaders
                </span>
              </h2>
              <p>Delivering excellence across the globe</p>
            </div>

            <div className="cam-live-brands cam-live-brands-reference">
              {brands.map((brand) => (
                <div className="cam-live-brand" key={brand.name}>
                  <img src={brand.image} alt={brand.name} loading="lazy" />
                  <span>{brand.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="modules" className="cam-live-section cam-live-modules-section">
          <div className="cam-live-section-container">
            <div className="section-header">
              <h2>
                Integrated{" "}
                <span className="cam-live-gradient-text">
                  Platform Modules
                </span>
              </h2>
              <p>
                One Connected Platform for All Your Operational Processes
              </p>
            </div>

            <div className="modules-grid cam-live-modules-grid">
              {modules.map((module) => (
                <article
                  className={`module-card cam-module-${module.tone}`}
                  key={module.title}
                >
                  <div className="module-card-flex">
                    <div>
                      <div className="module-image">
                        <img
                          src={module.image}
                          alt={module.title}
                          className="cam-live-module-image"
                          loading="lazy"
                        />

                        <div
                          className={`module-overlay cam-overlay-${module.tone}`}
                        />

                        <div className="module-float-icon">
                          <span>✦</span>
                        </div>
                      </div>

                      <h3 className="module-title">{module.title}</h3>

                      <ul className="module-features">
                        {module.features.map((feature) => (
                          <li className="module-feature" key={feature}>
                            <span
                              className={`cam-live-check cam-check-${module.tone}`}
                            >
                              ✓
                            </span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="module-btn-container">
                      <Link
                        to="/auth-login"
                        className={`module-btn cam-module-btn-${module.tone}`}
                      >
                        Get Started
                        <span>→</span>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer id="contact" className="cam-live-footer cam-reference-footer">
        <div className="footer-container">
          <div className="footer-grid cam-reference-footer-grid">
            <div>
              <div className="footer-brand">
                <img
                  src="/brand/camaroo-logo-white.png"
                  alt="Camaroo ERP"
                  className="cam-live-footer-logo"
                />
              </div>

              <p className="footer-description">
                Revolutionizing fisheries management with cutting-edge ERP
                solutions.
              </p>
            </div>

            <div>
              <h3 className="footer-section-title">Help & Support</h3>

              <a href="#contact" className="cam-whatsapp-link">
                <span className="cam-whatsapp-icon">◉</span>
                <span>WhatsApp Chat</span>
              </a>
            </div>

            <div>
              <h3 className="footer-section-title">Contact Us</h3>

              <form className="cam-footer-form" onSubmit={onSubmit}>
                <div className="cam-footer-form-row">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={form.name}
                    onChange={onChange}
                  />

                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={form.email}
                    onChange={onChange}
                  />
                </div>

                <textarea
                  name="message"
                  placeholder="Type your message"
                  rows={4}
                  value={form.message}
                  onChange={onChange}
                />

                <button type="submit" className="cam-footer-send">
                  Send Message
                  <span>↗</span>
                </button>
              </form>
            </div>
          </div>

          <div className="footer-bottom">
            <span>© {new Date().getFullYear()} Camaroo. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
